import type { Editor } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { useEditorState } from '@tiptap/react';
import { Columns2Icon, ListIcon, Rows2Icon } from 'lucide-react';
import { useCallback, useMemo } from 'react';

import type { DropdownOption } from '@/components/ui/tiptap/tiptap-ui-primitives/dropdown-menu';

import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { isNodeInSchema, shouldShow } from '@/lib/tiptap';

import type { ToggleHeaderButtonProps, ToggleHeaderType } from '.';

export const toggleHeaderOptions: DropdownOption<ToggleHeaderType>[] = [
  {
    label: 'Nagłówek w pierwszej kolumnie',
    type: 'columnHeader',
    icon: Columns2Icon,
  },
  {
    label: 'Nagłówek w pierwszym wierszu',
    type: 'rowHeader',
    icon: Rows2Icon,
  },
  {
    label: 'Nagłówek w zaznaoczonej komórce',
    type: 'cellHeader',
    icon: Rows2Icon,
  },
];

function useIsActiveElement(editor: Editor | null, type: ToggleHeaderType): boolean {
  return (
    useEditorState({
      editor,
      selector: (ctx) => {
        return ctx.editor?.isActive(type) ?? false;
      },
    }) ?? false
  );
}

function toggleHeaderDropdownItem(editor: Editor | null, type: ToggleHeaderType): void {
  if (!editor) return;
  switch (type) {
    case 'columnHeader':
      editor.chain().focus().toggleHeaderColumn().run();
      break;
    case 'rowHeader':
      editor.chain().focus().toggleHeaderRow().run();
      break;
    case 'cellHeader':
      editor.chain().focus().toggleHeaderCell().run();
      break;
  }
}

function getOption(type: ToggleHeaderType): DropdownOption<ToggleHeaderType> | undefined {
  return toggleHeaderOptions.find((option) => option.type === type);
}

function useToggleHeaderState(editor: Editor | null, type: ToggleHeaderType) {
  const isActive: boolean = useIsActiveElement(editor, type);
  const option: DropdownOption<ToggleHeaderType> | undefined = getOption(type);

  return { isActive, option };
}

function useHandleClick(onClick: ToggleHeaderButtonProps['onClick'], callback: () => void) {
  return useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      onClick?.(e);
      if (!e.defaultPrevented) {
        callback();
      }
    },
    [onClick, callback]
  );
}

export function ToggleHeaderButton(toggleHeaderButtonProps: ToggleHeaderButtonProps) {
  const { editor, type, onClick, className, children, ...props } = toggleHeaderButtonProps;
  const { isActive, option } = useToggleHeaderState(editor, type);
  const inSchema: boolean = isNodeInSchema('table', editor);

  const Icon = option?.icon || ListIcon;

  const handleClick = useHandleClick(onClick, () => toggleHeaderDropdownItem(editor, type));

  const show: boolean = useMemo(() => {
    return shouldShow([!!editor, inSchema]);
  }, [editor, inSchema]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenuItem
      data-active-state={isActive ? 'on' : 'off'}
      aria-label={option?.label || type}
      aria-pressed={isActive}
      onClick={handleClick}
      className={cn('group aria-pressed:text-text-primary aria-pressed:bg-fill-secondary/60 aria-pressed:hover:bg-fill-secondary/80', className)}
      {...props}
    >
      {children || (
        <>
          <Icon className="group:aria-pressed:text-text-primary" />
          {option?.label || type}
        </>
      )}
    </DropdownMenuItem>
  );
}
